import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate, formatCurrency, getStatusColor } from '@/lib/utils'
import { Plus, X, FolderKanban, Calendar, User, TrendingUp } from 'lucide-react'
import { toast } from 'sonner'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  not_started: { label: 'Not started', color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
  planning:    { label: 'Planning',    color: 'var(--info)',         bg: 'var(--info-dim)' },
  in_progress: { label: 'In progress', color: 'var(--accent-text)',  bg: 'var(--accent-dim)' },
  review:      { label: 'Review',      color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  complete:    { label: 'Complete',    color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  on_hold:     { label: 'On hold',     color: 'var(--danger)',       bg: 'var(--danger-dim)' },
  cancelled:   { label: 'Cancelled',   color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)' },
}

const PROJECT_COLORS = ['#7c3aed', '#0c87de', '#00d9a3', '#ff6b6b', '#fbbf24', '#f472b6', '#06b6d4', '#84cc16']

function NewProjectModal({ onClose, onCreated, contacts }: { onClose: () => void; onCreated: (id: string) => void; contacts: any[] }) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({ name: '', contact_id: '', contact_name: '', owner_name: '', due_date: '', color: '#7c3aed', status: 'not_started' })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const contact = contacts.find(c => c.id === e.target.value)
    if (contact) setForm(p => ({ ...p, contact_id: contact.id, contact_name: `${contact.first_name} ${contact.last_name || ''}`.trim() }))
  }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Project name is required'); return }
    setSaving(true)
    const { data, error } = await supabase.from('projects').insert({
      name: form.name,
      contact_id: form.contact_id || null,
      contact_name: form.contact_name || null,
      owner_name: form.owner_name || null,
      due_date: form.due_date || null,
      color: form.color,
      status: form.status,
      progress_percent: 0,
      budget_spent: 0,
      workspace_id: workspace!.id,
    }).select().single()
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Project created')
    onCreated(data.id)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New project</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Project name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Website redesign" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Client</label>
            <select onChange={handleContactSelect} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a contact...</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Owner</label>
              <input value={form.owner_name} onChange={e => set('owner_name', e.target.value)} placeholder="Your name" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Due date</label>
              {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
              <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} style={{ ...inp, colorScheme: 'dark' }} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Color</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {PROJECT_COLORS.map(color => (
                <button key={color} onClick={() => set('color', color)}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: color, border: form.color === color ? '3px solid var(--ink-primary)' : '3px solid transparent', cursor: 'pointer', transition: 'border 150ms ease' }} />
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Creating...' : 'Create project'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function ProjectCard({ project, onClick }: { project: any; onClick: () => void }) {
  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.not_started
  const isOverdue = project.due_date && new Date(project.due_date) < new Date() && !['complete', 'cancelled'].includes(project.status)

  return (
    <motion.div whileHover={{ y: -2 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', transition: 'border-color 200ms ease' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = `color-mix(in srgb, ${project.color || 'var(--accent)'} var(--status-tint-strong), transparent)`}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
      {/* Color bar — project.color is DB user content (color picker); themed fallback for null */}
      <div style={{ height: 3, background: project.color || 'var(--accent)' }} />
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.01em', flex: 1, paddingRight: 8 }}>{project.name}</h3>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{cfg.label}</span>
        </div>

        {project.contact_name && (
          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
            <User style={{ width: 11, height: 11 }} />{project.contact_name}
          </p>
        )}

        {/* Progress bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Progress</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: project.progress_percent > 0 ? project.color : 'var(--ink-tertiary)' }}>{project.progress_percent}%</span>
          </div>
          <div style={{ height: 4, background: 'var(--hover-bg)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${project.progress_percent}%`, background: project.color || 'var(--accent)', borderRadius: 100, transition: 'width 300ms ease' }} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {project.due_date ? (
            <span style={{ fontSize: 11, color: isOverdue ? 'var(--danger)' : 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar style={{ width: 11, height: 11 }} />
              {isOverdue ? 'Overdue · ' : ''}{formatDate(project.due_date, 'MMM d')}
            </span>
          ) : <span />}
          {project.owner_name && (
            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{project.owner_name}</span>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default function ProjectsHub() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showNewModal, setShowNewModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('projects').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-projects', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, company_name').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const metrics = useMemo(() => ({
    total: projects.length,
    active: projects.filter((p: any) => ['planning', 'in_progress', 'review'].includes(p.status)).length,
    complete: projects.filter((p: any) => p.status === 'complete').length,
    overdue: projects.filter((p: any) => p.due_date && new Date(p.due_date) < new Date() && !['complete', 'cancelled'].includes(p.status)).length,
  }), [projects])

  const filtered = useMemo(() => {
    let list = [...projects]
    if (statusFilter === 'active') list = list.filter((p: any) => ['planning', 'in_progress', 'review'].includes(p.status))
    else if (statusFilter === 'complete') list = list.filter((p: any) => p.status === 'complete')
    else if (statusFilter === 'overdue') list = list.filter((p: any) => p.due_date && new Date(p.due_date) < new Date() && !['complete', 'cancelled'].includes(p.status))
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((p: any) => p.name.toLowerCase().includes(q) || p.contact_name?.toLowerCase().includes(q))
    }
    return list
  }, [projects, statusFilter, search])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Projects</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{projects.length} total · {metrics.active} active</p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New project
          </button>
        </div>

        {/* Metrics — auto-fit so cards reflow on narrow viewports instead
            of compressing 4-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total', value: metrics.total, color: 'var(--accent)', filter: 'all' },
            { label: 'Active', value: metrics.active, color: 'var(--info)', filter: 'active' },
            { label: 'Complete', value: metrics.complete, color: 'var(--positive)', filter: 'complete' },
            { label: 'Overdue', value: metrics.overdue, color: 'var(--danger)', filter: 'overdue' },
          ].map(m => (
            <div key={m.label} onClick={() => setStatusFilter(m.filter)}
              style={{ background: statusFilter === m.filter ? `color-mix(in srgb, ${m.color} var(--status-tint-faint), transparent)` : 'var(--surface)', border: `1px solid ${statusFilter === m.filter ? `color-mix(in srgb, ${m.color} var(--status-tint-strong), transparent)` : 'var(--border-subtle)'}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'all 150ms ease' }}>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>{m.label}</p>
              <p style={{ fontSize: 24, fontWeight: 800, color: m.color, margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..."
            style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
            onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
            onBlur={e => e.currentTarget.style.borderColor='var(--border-subtle)'} />
          {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <FolderKanban style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
              {search || statusFilter !== 'all' ? 'No projects match your filters' : 'No projects yet'}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
              {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first project to get started'}
            </p>
            {!search && statusFilter === 'all' && (
              <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                <Plus style={{ width: 14, height: 14 }} /> New project
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {filtered.map((project: any) => (
              <ProjectCard key={project.id} project={project} onClick={() => navigate(`/app/projects/${project.id}`)} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewProjectModal
            onClose={() => setShowNewModal(false)}
            onCreated={(id) => navigate(`/app/projects/${id}`)}
            contacts={contacts}
          />
        )}
      </AnimatePresence>
    </div>
  )
}